import React from "react";
import Card from "../../../../components/ui/Card";
import Button from "../../../../components/ui/Button";
import { PlusCircle, CheckCircle, XCircle } from "lucide-react";
import { mockCreator, platformVettedCauses } from "../../../../data/mockData";
import RecentActivityList from "../../../../components/ui/RecentActivityList";

const StatusBadge = ({ status }) => {
  const statusClasses = {
    Approved: "bg-green-100 text-green-700",
    Pending: "bg-yellow-100 text-yellow-700",
  };
  return (
    <span
      className={`px-2 py-1 text-xs font-semibold rounded-full ${statusClasses[status]}`}
    >
      {status}
    </span>
  );
};

const DonationCampaignsTab = ({
  campaigns,
  onCreate,
  supportedCauseIds,
  onToggleSupport,
}) => {
  const donationTransactions = mockCreator.mockTransactions
    .filter((txn) => txn.type === "Donation")
    .slice(0, 5);

  const totalRaised = campaigns.reduce(
    (acc, camp) => acc + (camp.raisedETB || 0),
    0
  );
  const activeCampaignsCount = campaigns.filter(
    (c) => c.status === "Approved"
  ).length;

  return (
    <div className="space-y-8">
      {/* --- Analytics Cards --- */}
      <div className="grid grid-cols-1 @md:grid-cols-3 gap-4">
        <Card>
          <p className="text-sm text-slate-500">Total Raised (All Campaigns)</p>
          <p className="text-2xl font-bold text-slate-800">
            {totalRaised.toLocaleString()} ETB
          </p>
        </Card>
        <Card>
          <p className="text-sm text-slate-500">Active Campaigns</p>
          <p className="text-2xl font-bold text-slate-800">
            {activeCampaignsCount}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-slate-500">Total Supporters</p>
          <p className="text-2xl font-bold text-slate-800">1,250</p>
        </Card>
      </div>

      {/* --- Creator's Submitted Campaigns Section --- */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-slate-800">
            Your Submitted Causes
          </h3>
          <Button variant="primary" size="sm" onClick={onCreate}>
            <PlusCircle size={16} className="mr-2" /> Submit a Cause
          </Button>
        </div>
        <div className="grid grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3 gap-4">
          {campaigns.map((camp) => (
            <div
              key={camp.id}
              className="p-4 border border-slate-200 rounded-lg flex flex-col h-full"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-semibold text-slate-800 line-clamp-1" title={camp.title}>{camp.title}</p>
                  <p className="text-sm text-slate-500 line-clamp-1">
                    Beneficiary: {camp.beneficiary}
                  </p>
                </div>
                <StatusBadge status={camp.status} />
              </div>
              <div className="mt-auto">
                <div className="flex justify-between text-sm text-slate-600 mb-1">
                  {/* ✨ FIX: Use default values to prevent crash ✨ */}
                  <span>
                    Raised: {(camp.raisedETB || 0).toLocaleString()} ETB
                  </span>
                  <span>Goal: {(camp.goalETB || 0).toLocaleString()} ETB</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2.5">
                  <div
                    className="bg-primary h-2.5 rounded-full"
                    style={{
                      width: `${
                        ((camp.raisedETB || 0) / (camp.goalETB || 1)) * 100
                      }%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* --- Platform-Vetted Causes Section --- */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          Support an Official Platform Cause
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          Choose which official causes to feature and actively support on your
          public profile.
        </p>
        <div className="grid grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3 gap-6">
          {platformVettedCauses.map((cause) => {
            const isSupported = supportedCauseIds.includes(cause.id);
            return (
              <Card
                key={cause.id}
                className={`flex flex-col ${
                  isSupported ? "border-primary" : ""
                }`}
              >
                <div className="flex items-center mb-3">
                  <img
                    src={cause.logoUrl}
                    alt={`${cause.organization} logo`}
                    className="w-10 h-10 rounded-full mr-4"
                  />
                  <div>
                    <p className="font-semibold text-slate-800">
                      {cause.title}
                    </p>
                    <p className="text-xs text-slate-500">
                      {cause.organization}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 flex-grow mb-4">
                  {cause.description}
                </p>

                {isSupported && (
                  <div className="mt-auto mb-4">
                    <div className="flex justify-between text-sm text-slate-600 mb-1">
                      <span>
                        Raised: {(cause.raisedETB || 0).toLocaleString()} ETB
                      </span>
                      <span>
                        Goal: {(cause.goalETB || 0).toLocaleString()} ETB
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2.5">
                      <div
                        className="bg-primary h-2.5 rounded-full"
                        style={{
                          width: `${
                            ((cause.raisedETB || 0) / (cause.goalETB || 1)) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}

                <Button
                  variant={isSupported ? "secondary" : "primary"}
                  className="w-full mt-auto"
                  onClick={() => onToggleSupport(cause.id)}
                >
                  {isSupported ? (
                    <>
                      <XCircle size={16} className="mr-2" /> Stop Supporting
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} className="mr-2" /> Support this
                      Cause
                    </>
                  )}
                </Button>
              </Card>
            );
          })}
        </div>
      </div>

      {/* --- Recent Activity List --- */}
      <RecentActivityList
        title="Recent Donation Activity"
        transactions={donationTransactions}
      />
    </div>
  );
};

export default DonationCampaignsTab;
